import React from 'react';

const PrivacyPolicyPage = () => {
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Using Tailwind classes for styling, assuming they are available in the project.
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8 text-gray-300">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Privacy Policy for TubeTactix
        </h1>
        <p className="mt-4 text-lg text-gray-400">
          Last updated: {formattedDate}
        </p>
      </div>

      <div className="space-y-10">
        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            1. Introduction
          </h2>
          <p>
            Welcome to TubeTactix ("we," "our," "us"). We are committed to
            protecting your privacy. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use our web
            application (the "Service"). Please read this privacy policy
            carefully. If you do not agree with the terms of this privacy policy,
            please do not access the Service.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            2. Information We Collect
          </h2>
          <p className="mb-4">
            We may collect information about you in a variety of ways. The
            information we may collect via the Service includes:
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2 text-white">
            Personal Data
          </h3>
          <p className="mb-4">
            When you register for our Service using "Sign in with Google," we
            collect personally identifiable information that Google's
            authentication service provides, such as your name, email address,
            and profile picture URL. We use this information solely to create and
            manage your user account.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2 text-white">
            IP Address
          </h3>
          <p className="mb-4">
            To prevent abuse of our credit system and to ensure fair use, we
            collect the IP address from which your account is first created. This
            data is used for security purposes only.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2 text-white">
            Derivative Data
          </h3>
          <p className="mb-4">
            We may collect information our servers automatically collect when you
            access the Service, such as your IP address (for rate-limiting and
            security), browser type, and the times you access our Service.
          </p>
          <h3 className="text-xl font-bold mt-6 mb-2 text-white">
            User-Generated Content
          </h3>
          <p>
            We store the data you voluntarily provide to us when using our tools.
            This includes:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-4">
            <li>
              The video scripts you submit for analysis to the Script
              Intelligence tool.
            </li>
            <li>
              The list of YouTube channels you choose to track in the Channel
              Analyzer tool.
            </li>
          </ul>
          <h3 className="text-xl font-bold mt-6 mb-2 text-white">
            Feedback Submissions
          </h3>
          <p>
            The comments and suggestions you voluntarily provide through our feedback form.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            3. How We Use Your Information
          </h2>
          <p>
            Having accurate information about you permits us to provide you with a
            smooth, efficient, and customized experience. Specifically, we may
            use information collected about you via the Service to:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-4">
            <li>Create and manage your account.</li>
            <li>
              Provide the core functionality of our research and strategy tools.
            </li>
            <li>
              Manage your credit system, including daily resets of free credits
              and balances of purchased credits.
            </li>
            <li>
              Monitor usage to prevent fraudulent activity and abuse of the
              Service.
            </li>
            <li>
              Process payments and subscriptions through our third-party payment
              processor, Stripe. We do not store or have access to your credit
              card information.
            </li>
            <li>
              Email you regarding your account, such as welcome messages,
              purchase confirmations, or important service notifications.
            </li>
            <li>
              Contact you in response to feedback or suggestions you have provided.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            4. Data Processing and Third-Party Services
          </h2>
          <p>
            The core functionality of TubeTactix relies on the use of third-party
            APIs. By using our Service, you acknowledge and agree that your data
            may be processed by these services:
          </p>
          <ul className="list-disc list-inside space-y-2 mt-4">
            <li>
              <strong>Google API Services (YouTube Data API v3):</strong> When
              you use our Keyword Research and Channel Analyzer tools, we make
              requests to the YouTube Data API. These requests are made using our
              server's API key and are not authenticated as you. We do not access
              your personal YouTube account data. Your use of features that rely
              on this API is subject to the{" "}
              <a
                href="http://www.google.com/policies/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline"
              >
                Google Privacy Policy
              </a>
              .
            </li>
            <li>
              <strong>Google Generative AI (Gemini API):</strong> When you use
              our AI-powered features, such as the Script Intelligence or Title
              Generation tools, the content you submit (e.g., your video script
              or keywords) is sent to the Google Gemini API for processing. We do
              not use this content to train our own models.
            </li>
            <li>
              <strong>Stripe:</strong> For payment processing for our Pro plan
              and credit packs. We send necessary billing information to Stripe
              to process transactions.
            </li>
            <li>
              <strong>Google Analytics & Google Tag Manager:</strong> We use
              Google Analytics to understand how users interact with our
              Service. This helps us improve features, identify issues, and
              make data-driven decisions. Google Tag Manager is used to manage
              these analytics scripts. When you provide consent, anonymous
              usage data (such as page views, button clicks, and session
              duration) is sent to Google Analytics.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            5. Disclosure of Your Information
          </h2>
          <p>
            We do not sell, trade, or otherwise transfer your Personally
            Identifiable Information to outside parties. We may share information
            we have collected about you in certain situations, such as when
            required by law or to protect our rights.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            6. Cookies and Consent Management
          </h2>
          <p>
            We use cookies and similar technologies to operate and analyze our
            Service. A cookie is a small file placed on your device. Our primary
            use of cookies is for analytics, as described above.
          </p>
          <p className="mt-4">
            You have control over this data collection. When you first visit our
            Service, a banner will ask for your consent to use cookies for
            analytics. If you accept, Google Analytics will collect data as
            described. If you decline, Google's Consent Mode ensures that no
            cookies are stored for analytics, and any data sent to Google for
            basic measurement is anonymized and not tied to you.
          </p>
          <p>
            We do not sell, trade, or otherwise transfer your Personally
            Identifiable Information to outside parties. We may share information
            we have collected about you in certain situations, such as when
            required by law or to protect our rights.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            7. Security of Your Information
          </h2>
          <p>
            We use administrative, technical, and physical security measures to
            help protect your personal information. While we have taken
            reasonable steps to secure the personal information you provide to
            us, please be aware that despite our efforts, no security measures
            are perfect or impenetrable, and no method of data transmission can
            be guaranteed against any interception or other type of misuse.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-white">
            8. Contact Us
          </h2>
          <p>
            If you have questions or comments about this Privacy Policy, please
            contact us at: contact@tubetactix.com.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;